import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Select } from 'components/Shared';
import type { SelectOption } from 'contracts/Common';
import {
  DeleteWarehouseCompanyActions as DeleteActions,
  CreateWarehouseCompanyActions as CreateActions,
} from 'store/ducks/warehouseCompanies';
import { useValidation } from 'hooks';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import * as Yup from 'yup';
import * as S from './styles';
import { WarehouseCompany } from 'contracts/Warehouses';
import { Formatter } from 'utils';

export interface Ref {
  resetForm: () => void;
}

interface Props {
  onUpdate?: () => void;
  warehouseId: string;
}

interface ICompaniesManager
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

interface ItemProps {
  company: WarehouseCompany['company'];
  id: number;
}

const CompaniesManager: ICompaniesManager = forwardRef<Ref, Props>(
  ({ warehouseId, onUpdate }, ref) => {
    const { data: warehouse } = useSelector(
      (state: RootState) => state.fetchWarehouse
    );

    const dispatch: AppDispatch = useDispatch();
    const formRef = useRef<FormHandles>(null);

    const { handleFormErrors, handleApiErrors } = useValidation();

    /**
     * this component will not fetch companies here,
     * we lifted it to a upper level (DocksManager component)
     * to avoid dispatching multiple times
     * */
    const { loading: loadingCompanies, data: companies } = useSelector(
      (state: RootState) => state.listCompanies
    );

    const { loading: creating, validationErrors } = useSelector(
      (state: RootState) => state.createWarehouseCompany
    );

    const { id: deletingId } = useSelector(
      (state: RootState) => state.deleteWarehouseCompany
    );

    /**
     * we will also filter the list of companies to only show
     * the ones that are not already bound to the dock
     * */

    const companyOptions: SelectOption[] | undefined = useMemo(() => {
      if (!companies?.length) return undefined;

      return companies
        .filter((company) =>
          warehouse?.warehouseCompanies
            ? !warehouse.warehouseCompanies.find(
                (warehouseCompany) => warehouseCompany.company.id === company.id
              )
            : true
        )
        .map((company) => ({
          value: company.id,
          label: `${company.tradeName} - ${Formatter.document(
            company.document,
            company.documentType
          )}`,
        }));
    }, [companies, warehouse]);

    const onDelete = useCallback(
      (id?: number) => dispatch(DeleteActions.request(id, onUpdate)),
      [dispatch, onUpdate]
    );

    const onSuccess = useCallback((): void => {
      onUpdate && onUpdate();
      formRef.current?.reset();
    }, [onUpdate]);

    const onSubmit = useCallback(
      async (data: any): Promise<void> => {
        try {
          formRef.current?.setErrors({});
          const schema = Yup.object().shape({
            companyId: Yup.number()
              .typeError('Selecione o cliente')
              .required('Selecione o cliente'),
          });
          const validData = await schema.validate(data, {
            abortEarly: false,
          });
          dispatch(
            CreateActions.request({ ...validData, warehouseId }, onSuccess)
          );
        } catch (error) {
          handleFormErrors(error, formRef);
        }
      },
      [dispatch, handleFormErrors, onSuccess, warehouseId]
    );

    const Item = useCallback(
      ({ company, id }: ItemProps): JSX.Element => {
        const {
          addressCity,
          addressState,
          document,
          id: companyId,
          tradeName,
        } = company;

        return (
          <S.ListItem>
            <S.Column>{companyId}</S.Column>
            <S.Column>{tradeName}</S.Column>
            <S.Column>{Formatter.cnpj(document)}</S.Column>
            <S.Column>{addressCity}</S.Column>
            <S.Column>{addressState}</S.Column>
            <S.ActionsColumn>
              <S.ActionButton
                disabled={deletingId === companyId}
                onClick={() => onDelete(id)}
                mood="danger"
              >
                {deletingId === companyId ? (
                  <S.ActivityIndicator />
                ) : (
                  <S.TrashIcon />
                )}
              </S.ActionButton>
            </S.ActionsColumn>
          </S.ListItem>
        );
      },
      [deletingId, onDelete]
    );

    useImperativeHandle(
      ref,
      () => ({
        resetForm: () => {
          formRef.current?.reset();
          formRef.current?.setErrors({});
        },
      }),
      []
    );

    useEffect(() => {
      handleApiErrors(validationErrors, formRef);
    }, [handleApiErrors, validationErrors]);

    return (
      <S.Container>
        <Form ref={formRef} onSubmit={onSubmit}>
          <S.FormRow>
            <Select
              name="companyId"
              label="Cliente"
              options={companyOptions}
              isLoading={loadingCompanies}
              menuPortalTarget={document.body}
              menuPlacement="top"
            />
            <S.Button type="submit">
              {creating ? <S.ActivityIndicator /> : 'Adicionar'}
            </S.Button>
          </S.FormRow>
        </Form>
        {!warehouse?.warehouseCompanies?.length ? (
          <S.EmptyListPlaceholder>
            Esse armazém ainda não possui restrições para clientes
          </S.EmptyListPlaceholder>
        ) : (
          <>
            <S.ListHeader>
              <div>ID</div>
              <div>NOME</div>
              <div>CNPJ</div>
              <div>CIDADE</div>
              <div>ESTADO</div>
            </S.ListHeader>
            {warehouse?.warehouseCompanies?.map((props) => (
              <Item {...props} key={props.id} />
            ))}
          </>
        )}
      </S.Container>
    );
  }
);

export default CompaniesManager;
